import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type AppletSizing,
  type AppletTheme,
  messages,
  parseAppletToHostMessage,
} from '@kudu/applet-protocol';

import { useTheme } from '@/theme/useTheme';

import { buildAppletSrc, buildRawAppletSrc, getAppletOrigin } from './appletOrigin';
import { ResetAppletDialog } from './ResetAppletDialog';
import styles from './AppletFrame.module.css';

interface AppletFrameProps {
  appletId: string;
  version: string;
  config: Record<string, unknown>;
  title: string;
  /**
   * Path-based applet source (a full applet-origin URL). When set, it is used
   * instead of the `/<appletId>/<version>/` root — for applets like JupyterLite
   * whose embed is a deep path.
   */
  srcOverride?: string;
  /** Sandbox tokens for this applet; defaults to `allow-scripts` only. */
  sandbox?: string;
  /** Fixed height in px (IDE-style applets); overrides aspect/dynamic sizing. */
  fixedHeight?: number;
  /**
   * When true, the host shows a confirmed "Reset" control that asks the applet
   * to discard local work and reload to the original (via the `reset` message).
   */
  resettable?: boolean;
  /** Label for the reset control, e.g. "Reset notebook". Defaults to "Reset". */
  resetLabel?: string;
}

/** Map the app's semantic design tokens onto the applet theme token contract. */
const THEME_TOKEN_SOURCES: Record<string, string> = {
  bg: '--color-bg-primary',
  surface: '--color-bg-elevated',
  text: '--color-text-primary',
  border: '--color-border-default',
  accent: '--color-accent',
};

const DEFAULT_ASPECT_RATIO = 3 / 2;
const MIN_DYNAMIC_HEIGHT = 120;
const MAX_DYNAMIC_HEIGHT = 4000;

function readTheme(mode: 'light' | 'dark'): AppletTheme {
  const tokens: Record<string, string> = {};
  if (typeof window !== 'undefined') {
    const computed = getComputedStyle(document.documentElement);
    for (const [name, source] of Object.entries(THEME_TOKEN_SOURCES)) {
      const value = computed.getPropertyValue(source).trim();
      if (value) {
        tokens[name] = value;
      }
    }
  }
  return { mode, tokens };
}

/**
 * Host for a Kudu applet. Renders the applet in a cross-origin sandboxed iframe
 * (`allow-scripts` only — never `allow-same-origin`), performs the init
 * handshake, keeps theme in sync, sizes the frame, lazy-mounts on scroll, and
 * brokers events. Persistence messages hit a logged, acknowledged-unsupported
 * seam — see the TODO in `handleMessage`.
 */
export function AppletFrame({
  appletId,
  version,
  config,
  title,
  srcOverride,
  sandbox,
  fixedHeight,
  resettable = false,
  resetLabel = 'Reset',
}: AppletFrameProps) {
  const { resolvedTheme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [mounted, setMounted] = useState(false);
  const [ready, setReady] = useState(false);
  const [sizing, setSizing] = useState<AppletSizing | null>(null);
  const [dynamicHeight, setDynamicHeight] = useState<number | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  const appletOrigin = getAppletOrigin();
  const src = srcOverride
    ? buildRawAppletSrc(srcOverride)
    : buildAppletSrc({ appletId, version });

  const postToApplet = useCallback(
    (message: unknown) => {
      if (appletOrigin) {
        iframeRef.current?.contentWindow?.postMessage(message, appletOrigin);
      }
    },
    [appletOrigin],
  );

  // Confirmed reset: ask the applet to discard local work and reload to the
  // original. It re-handshakes afterward, so drop back to the loading state.
  const handleReset = useCallback(() => {
    setResetOpen(false);
    setReady(false);
    postToApplet(messages.reset());
  }, [postToApplet]);

  // Preconnect to the applet origin so the first lazy load is fast.
  useEffect(() => {
    if (!appletOrigin) {
      return;
    }
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = appletOrigin;
    link.crossOrigin = '';
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [appletOrigin]);

  // Lazy-mount the iframe only when the embed nears the viewport.
  useEffect(() => {
    if (mounted) {
      return;
    }
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setMounted(true); // no observer support → load immediately
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [mounted]);

  // Validate and handle every inbound message from the applet.
  useEffect(() => {
    if (!mounted || !appletOrigin) {
      return;
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== appletOrigin) {
        return; // foreign origin — discard
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return; // not from our iframe — discard
      }
      const message = parseAppletToHostMessage(event.data);
      if (!message) {
        return;
      }
      switch (message.type) {
        case 'ready':
          setSizing(message.sizing ?? { mode: 'aspect-ratio', ratio: DEFAULT_ASPECT_RATIO });
          setReady(true);
          postToApplet(messages.init(config, readTheme(resolvedTheme)));
          break;
        case 'resize':
          setDynamicHeight(
            Math.min(Math.max(message.height, MIN_DYNAMIC_HEIGHT), MAX_DYNAMIC_HEIGHT),
          );
          break;
        case 'report-event':
          // Fire-and-forget telemetry. v1: log; Phase 2 routes to analytics.
          if (import.meta.env.DEV) {
            console.info('[applet:event]', appletId, message.event);
          }
          break;
        case 'save-state':
        case 'load-state':
          // TODO(applets Phase 2 — §5 data seam): persistence is unsupported in
          // v1. The real implementation routes save/load through this parent
          // page's authenticated Kudu save flow (debounced, on checkpoints),
          // WITHOUT handing the applet any token. Until then, acknowledge as
          // unsupported so the applet's SDK resolves cleanly.
          if (import.meta.env.DEV) {
            console.info('[applet:state-seam]', appletId, message.type);
          }
          if (message.type === 'save-state') {
            postToApplet(messages.saveStateAck(false));
          } else {
            postToApplet(messages.loadStateResult(message.requestId, false, null));
          }
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [mounted, appletOrigin, config, resolvedTheme, postToApplet, appletId]);

  // Follow live app theme changes once the applet is ready.
  useEffect(() => {
    if (ready) {
      postToApplet(messages.theme(readTheme(resolvedTheme)));
    }
  }, [ready, resolvedTheme, postToApplet]);

  if (!src || !appletOrigin) {
    return (
      <p className={styles.fallback} role="note">
        This interactive applet is unavailable.
      </p>
    );
  }

  const isDynamic = sizing?.mode === 'dynamic';
  const ratio = sizing?.mode === 'aspect-ratio' ? sizing.ratio : DEFAULT_ASPECT_RATIO;
  const frameStyle: React.CSSProperties = fixedHeight
    ? { height: fixedHeight }
    : isDynamic
      ? { height: dynamicHeight ?? MIN_DYNAMIC_HEIGHT }
      : { aspectRatio: String(ratio) };

  // Drop a leading "Reset " so the dialog can talk about the noun ("notebook").
  const resetNoun = resetLabel.replace(/^reset\s+/i, '').trim() || 'applet';

  return (
    <div className={styles.root}>
      {resettable && ready ? (
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.resetButton}
            onClick={() => setResetOpen(true)}
          >
            <span aria-hidden>↺</span>
            {resetLabel}
          </button>
        </div>
      ) : null}

      <div className={styles.wrapper} ref={containerRef} style={frameStyle}>
        {mounted ? (
          <>
            <iframe
              ref={iframeRef}
              className={styles.frame}
              sandbox={sandbox ?? 'allow-scripts'}
              allow="fullscreen"
              src={src}
              title={title}
            />
            {!ready ? (
              <p className={styles.loading} role="status" aria-live="polite">
                Loading {title}…
              </p>
            ) : null}
          </>
        ) : (
          <button
            type="button"
            className={styles.poster}
            onClick={() => setMounted(true)}
            aria-label={`Load interactive applet: ${title}`}
          >
            <span aria-hidden>▶</span>
            <span>{title}</span>
            <span className={styles.posterHint}>Interactive applet</span>
          </button>
        )}
      </div>

      {resettable ? (
        <ResetAppletDialog
          noun={resetNoun}
          open={resetOpen}
          onOpenChange={setResetOpen}
          onConfirm={handleReset}
        />
      ) : null}
    </div>
  );
}
